import React from 'react'

import {
  arrayOf,
  object,
  shape,
  string,
  bool,
} from 'prop-types'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import i18n from 'Pods/Shared/I18nHome'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

import HeroAuthor from './Components/HeroAuthor'
import Search from './Components/Search/Search'
import TrendingButtons from './Components/TrendingButtons'
import { HomeHeroSectionMock } from './HomeHeroSectionMock'
import { getImageType } from './Static/images'

function HomeHeroSection({
  images = EMPTY_OBJECT,
  searchPlaceholder = '',
  locale = 'en',
  trending = EMPTY_ARRAY,
  extractingCoreStyles = false,
  author = null,
  newMainSearch = false,
  newSearchImages = EMPTY_OBJECT,
  newSearchAuthor = null,
  searchDropdown = null,
}) {
  // The home fast page renders actorless: `newMainSearch` is the signed-out SSR default. Once
  // /load-services.js resolves the per-actor flag, refine in place. The hero band is a fixed-height
  // (400px) container with an absolutely-positioned object-fit background, so swapping the
  // image/title/class cannot shift layout. See CLAUDE.md "SSR-stable rendering on fast pages".
  const pageConfig = usePageConfig({ listener: 'full-page-config-loaded', checkIfAvailable: false })
  const resolvedNewSearch = pageConfig ? Boolean(pageConfig.newMainSearchHomepage) : newMainSearch
  const activeImages = resolvedNewSearch ? newSearchImages : images
  const activeAuthor = resolvedNewSearch ? newSearchAuthor : author

  const desktopImage = activeImages?.desktop
  const desktopSmallImage = activeImages?.desktopSmall || activeImages?.desktop
  const tabletImage = activeImages?.tablet || activeImages?.desktopSmall || activeImages?.desktop
  const mobileImage = activeImages?.mobile
  const CreatedBy = i18n.t('hero.CreatedBy', {
    ns: 'new-home',
    lng: locale,
  })
  const aiSearchPlaceholder = i18n.t('search.placeholder.aiMode', {
    ns: 'new-home',
    lng: locale,
  })
  const heroAuthor = activeAuthor?.name ? activeAuthor : null
  const desktopIsDefault = desktopImage.includes('/assets/home/')
  const desktopSmallIsDefault = desktopSmallImage.includes('/assets/home/')
  const tabletIsDefault = tabletImage.includes('/assets/home/')
  const mobileIsDefault = mobileImage.includes('/assets/home/')
  const hasDefaultImage =
    desktopIsDefault || desktopSmallIsDefault || tabletIsDefault || mobileIsDefault
  const shouldShowAuthor = Boolean(activeAuthor?.name) || hasDefaultImage
  const trendingButtons = extractingCoreStyles ? HomeHeroSectionMock : trending

  const renderHero = (useNewSearch) => {
    const heroTitle = i18n.t(useNewSearch ? 'hero.titleNew' : 'hero.title', {
      ns: 'new-home',
      lng: locale,
    })
    const heroClass = useNewSearch ? 'home-hero home-hero--new-search' : 'home-hero'

    return (
      <div className={heroClass}>
        {shouldShowAuthor && (
          <HeroAuthor createdByText={CreatedBy} author={heroAuthor} />
        )}
        <picture className="home-hero__background">
          {[
            { media: '(min-width: 1600px)', src: desktopImage },
            { media: '(min-width: 960px)', src: desktopSmallImage },
            { media: '(min-width: 640px)', src: tabletImage },
          ].map(({ media, src }) => (
            <source
              key={media}
              media={media}
              srcSet={src}
              type={getImageType(src)}
            />
          ))}
          <img
            alt="3D model marketplace background"
            className="home-hero__background-image"
            src={mobileImage}
            // eslint-disable-next-line react/no-unknown-property
            fetchpriority="high"
            loading="eager"
          />
        </picture>

        <div className="home-hero__content">
          <h1 className="home-hero__title">{heroTitle}</h1>

          <div className="home-hero__search">
            <Search
              placeholder={useNewSearch ? aiSearchPlaceholder : searchPlaceholder}
              locale={locale}
              aiMode={useNewSearch}
              searchDropdown={searchDropdown}
            />

            {locale === 'en' && trendingButtons.length > 0 && Array.isArray(trendingButtons) && (
              <div className="home-hero__trending-buttons-container">
                <TrendingButtons
                  buttons={trendingButtons}
                  buttonSize="small"
                  customClass="home-hero__trending-button"
                  trackingContext="home-hero"
                  locale={locale}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // During the critical-CSS extraction pass, emit both the legacy and new-search heroes so both
  // class sets land in the static critical CSS. The new_main_search_homepage Flipper flag can then
  // flip at runtime without a FOUC (the critical CSS is baked once, not regenerated per flag flip).
  // Mirrors UpgradeHeader's extractingCoreStyles handling.
  if (extractingCoreStyles) {
    return (
      <>
        {renderHero(false)}
        {renderHero(true)}
      </>
    )
  }

  return renderHero(resolvedNewSearch)
}

const authorShape = shape({
  name: string,
  href: string,
  avatarUrl: string,
})

HomeHeroSection.propTypes = {
  images: object,
  searchPlaceholder: string,
  locale: string,
  author: authorShape,
  newSearchImages: object,
  newSearchAuthor: authorShape,
  trending: arrayOf(
    shape({
      label: string,
      href: string,
    })
  ),
  extractingCoreStyles: bool,
  newMainSearch: bool,
  searchDropdown: object,
}

export default HomeHeroSection
